import React, { useState } from "react";
import {
  Small,
  Divider,
  Flex,
  FlexItem,
  H5,
  StyledLink,
  Box,
  P,
  H6,
  Icon,
  Popover,
} from "orcs-design-system";
import { get } from "lodash";
import { useVersion } from "src/contexts/global/GlobalContext";
import { useTenantId } from "src/contexts/global/ApolloSettingsContext";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";
import { useAuth0 } from "src/contexts/auth0Provider";
import { isInternalSupportUser, auth0UserMetadataKey } from "src/services/auth";
import { isLocalEnvironment } from "src/services/localSettings";
import PATHS, { url } from "src/consts/urlPaths";
import { trackEvent } from "src/services/segment";
import { EVENT_TRACKING } from "src/consts/eventTracking";
import { copyToClipboard } from "src/util/embedUtils";
import { getFaviconUrl } from "src/util/url";

const GITHUB_REPO_URLS = {
  FRONTEND: "https://github.com/orchestrated-io/teamform-app-ui",
  BACKEND: "https://github.com/orchestrated-io/teamform-api",
};

const InfoSidebarContent = () => {
  const tenantId = useTenantId();
  const version = useVersion();
  const workspace = useWorkspace();
  const { user } = useAuth0();
  const isSupportUser = isInternalSupportUser(user) || isLocalEnvironment();
  const [copiedFE, setCopiedFE] = useState(false);
  const [copiedBE, setCopiedBE] = useState(false);
  const [faviconErrorFE, setFaviconErrorFE] = useState(false);
  const [faviconErrorBE, setFaviconErrorBE] = useState(false);

  const UI_VERSION = process.env.REACT_APP_GIT_SHA
    ? process.env.REACT_APP_GIT_SHA.slice(0, 7)
    : undefined;
  const UI_BUILD = process.env.REACT_APP_BUILD_NUMBER;
  const workspaceId = get(workspace, "id");
  const workspaceName = get(workspace, "name", "ACTIVE");

  // Get tenant name from user's tenant list
  // Only support users have access to tenantList metadata
  // Directory users and test accounts should NOT have tenantList, so we only access it for support users
  const canAccessTenantList = user && isInternalSupportUser(user);
  const tenantList = canAccessTenantList
    ? get(user, [auth0UserMetadataKey, "tenantList", "tenants"], [])
    : [];
  const matchedTenant = tenantList.find((t) => t.tenantId === tenantId);
  const tenantDisplayName =
    canAccessTenantList && matchedTenant?.name
      ? `${matchedTenant.name} [${tenantId}]`
      : tenantId;

  const parseBackendVersion = (versionString) => {
    if (!versionString) {
      return { commitHash: null, buildNumber: null };
    }
    const match = versionString.match(/^([a-f0-9]+)\s*\[(\d+)\]/);
    if (match) {
      return { commitHash: match[1], buildNumber: match[2] };
    }
    return { commitHash: null, buildNumber: null };
  };

  const { commitHash: beCommitHash, buildNumber: beBuildNumber } =
    parseBackendVersion(version);

  const handleCopy = async (compareUrl, setCopied) => {
    const success = await copyToClipboard(compareUrl);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const renderGitHubLink = (
    commitHash,
    buildNumber,
    repoUrl,
    copied,
    setCopied,
    faviconError,
    setFaviconError
  ) => {
    if (!commitHash || commitHash.toUpperCase() === "NETLIFY") {
      return null;
    }

    const compareUrl = `${repoUrl}/compare/${commitHash}...master`;
    const faviconUrl = getFaviconUrl(repoUrl);
    const showFavicon = faviconUrl && !faviconError;

    return (
      <Popover
        text={
          copied
            ? "Copied!"
            : "Click to copy compare URL to clipboard against master"
        }
        direction="top"
        width="200px"
      >
        <button
          type="button"
          onClick={() => handleCopy(compareUrl, setCopied)}
          data-testid={`github-compare-${buildNumber}`}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "2px 4px",
            marginLeft: "4px",
            display: "inline-flex",
            alignItems: "center",
            opacity: 0.6,
            transition: "opacity 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "0.6";
          }}
        >
          {showFavicon ? (
            <img
              src={faviconUrl}
              alt="GitHub"
              style={{ width: "14px", height: "14px", objectFit: "contain" }}
              onError={() => setFaviconError(true)}
            />
          ) : (
            <Icon icon={["fab", "github"]} style={{ fontSize: "14px" }} />
          )}
        </button>
      </Popover>
    );
  };

  return (
    <Flex flexDirection="column" justifyContent="space-between" height="100%">
      <FlexItem>
        <Box mb="r">
          <H5 fontWeight="bold">Get help</H5>
          <Box my="r">
            <Divider light />
          </Box>
          <H6 fontWeight="bold">Need Help?</H6>
          <P fontSize="1" mb="r">
            Search for answers on our support page, or submit a support request
            and our friendly team will be happy to assist you.
          </P>
          <Flex flexDirection="column" gap="s">
            <StyledLink
              href="https://get.help.teamform.co/servicedesk/customer/portals"
              target="_blank"
              button
              data-testid="support-page-link"
              onClick={() => {
                trackEvent(EVENT_TRACKING.URL_CLICKED, {
                  action: "support_page_link_clicked",
                });
              }}
            >
              Go to our support page
              <Icon
                icon={["fas", "external-link-alt"]}
                title="External link icon"
                ml="s"
                size="sm"
              />
            </StyledLink>

            <StyledLink
              href="https://status.teamform.co"
              target="_blank"
              button
              data-testid="status-page-link"
              onClick={() => {
                trackEvent(EVENT_TRACKING.URL_CLICKED, {
                  action: "status_page_link_clicked",
                });
              }}
            >
              Service status page
              <Icon
                icon={["fas", "external-link-alt"]}
                title="External link icon"
                ml="s"
                size="sm"
              />
            </StyledLink>
          </Flex>
        </Box>
      </FlexItem>
      <FlexItem>
        <Box mb="r">
          <Small>
            <StyledLink to={url(PATHS.API_DOCS)} data-testid="api-docs-link">
              <Icon mr="s" icon={["fas", "link"]} />
              API Docs
            </StyledLink>
          </Small>
          <H5 display="block" fontWeight="bold" mb="xs">
            Support information:
          </H5>
          {tenantId && (
            <Small display="block" mb="xs">
              <Flex display="inline-flex" alignItems="center">
                Tenant:
                <Box as="strong" ml="s" display="inline">
                  {tenantDisplayName}
                </Box>
              </Flex>
            </Small>
          )}
          {workspaceName && workspaceId && (
            <Small display="block" mb="xs">
              <Flex display="inline-flex" alignItems="center">
                Workspace:
                <Box as="strong" ml="s" display="inline">
                  {workspaceName} [{workspaceId}]
                </Box>
              </Flex>
            </Small>
          )}
          <Box display="block" mb="xs">
            <Small>
              <Flex display="inline-flex" alignItems="center">
                App UI version:
                <Box as="strong" ml="s" display="inline">
                  {UI_VERSION} [{UI_BUILD}]
                </Box>
                {isSupportUser &&
                  UI_VERSION &&
                  UI_VERSION.toUpperCase() !== "NETLIFY" &&
                  UI_VERSION.toUpperCase() !== "LOCAL" &&
                  renderGitHubLink(
                    UI_VERSION,
                    UI_BUILD,
                    GITHUB_REPO_URLS.FRONTEND,
                    copiedFE,
                    setCopiedFE,
                    faviconErrorFE,
                    setFaviconErrorFE
                  )}
              </Flex>
            </Small>
          </Box>
          <Box display="block" mb="xs">
            <Small>
              <Flex display="inline-flex" alignItems="center">
                App BE version:
                <Box as="strong" ml="s" display="inline">
                  {version}
                </Box>
                {isSupportUser &&
                  beCommitHash &&
                  version &&
                  version.toUpperCase() !== "NETLIFY" &&
                  version.toUpperCase() !== "LOCAL" &&
                  renderGitHubLink(
                    beCommitHash,
                    beBuildNumber,
                    GITHUB_REPO_URLS.BACKEND,
                    copiedBE,
                    setCopiedBE,
                    faviconErrorBE,
                    setFaviconErrorBE
                  )}
              </Flex>
            </Small>
          </Box>

          <Box mt="r">
            <svg
              width="200px"
              height="100%"
              viewBox="0 0 336 70"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M90.8786 51H95.3336V23.415H106.134V19.5H80.0786V23.415H90.8786V51ZM121.16 47.49C117.155 47.49 114.185 45.6 112.97 42.315L131.915 38.625C131.78 31.515 126.695 26.925 120.17 26.925C113.24 26.925 108.2 32.01 108.2 39.075C108.2 46.185 113.33 51.27 121.025 51.27C124.985 51.27 128.27 49.92 130.385 47.4L128.045 44.61C126.29 46.545 123.95 47.49 121.16 47.49ZM120.17 30.57C124.04 30.57 126.875 33 127.64 36.42L112.385 39.3V38.85C112.385 33.855 115.625 30.57 120.17 30.57ZM156.138 27.15V30.75C154.158 28.185 151.188 26.925 147.813 26.925C140.838 26.925 135.753 31.785 135.753 39.075C135.753 46.365 140.838 51.27 147.813 51.27C151.323 51.27 154.383 49.92 156.318 47.22V51H160.458V27.15H156.138ZM148.173 47.49C143.583 47.49 140.118 44.16 140.118 39.075C140.118 33.99 143.583 30.705 148.173 30.705C152.718 30.705 156.183 33.99 156.183 39.075C156.183 44.16 152.718 47.49 148.173 47.49ZM198.467 26.925C194.417 26.925 191.087 28.635 189.287 31.335C187.712 28.365 184.652 26.925 181.007 26.925C177.407 26.925 174.482 28.275 172.772 30.705V27.15H168.632V51H172.952V38.715C172.952 33.54 175.787 30.795 180.107 30.795C184.022 30.795 186.272 33.09 186.272 37.77V51H190.592V38.715C190.592 33.54 193.427 30.795 197.747 30.795C201.662 30.795 203.912 33.09 203.912 37.77V51H208.232V37.275C208.232 30.255 204.272 26.925 198.467 26.925ZM239.041 23.415V19.5H216.811V51H221.311V38.265H237.106V34.395H221.311V23.415H239.041ZM254.961 51.27C262.116 51.27 267.336 46.185 267.336 39.075C267.336 31.965 262.116 26.925 254.961 26.925C247.806 26.925 242.541 31.965 242.541 39.075C242.541 46.185 247.806 51.27 254.961 51.27ZM254.961 47.49C250.371 47.49 246.906 44.16 246.906 39.075C246.906 33.99 250.371 30.705 254.961 30.705C259.551 30.705 262.971 33.99 262.971 39.075C262.971 44.16 259.551 47.49 254.961 47.49ZM277.45 31.155V27.15H273.31V51H277.63V39.12C277.63 33.9 280.51 31.065 285.145 31.065C285.46 31.065 285.775 31.065 286.135 31.11V26.925C281.95 26.925 278.98 28.365 277.45 31.155ZM321.294 26.925C317.244 26.925 313.914 28.635 312.114 31.335C310.539 28.365 307.479 26.925 303.834 26.925C300.234 26.925 297.309 28.275 295.599 30.705V27.15H291.459V51H295.779V38.715C295.779 33.54 298.614 30.795 302.934 30.795C306.849 30.795 309.099 33.09 309.099 37.77V51H313.419V38.715C313.419 33.54 316.254 30.795 320.574 30.795C324.489 30.795 326.739 33.09 326.739 37.77V51H331.059V37.275C331.059 30.255 327.099 26.925 321.294 26.925Z"
                fill="black"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M34.7252 69.4503C53.9033 69.4503 69.4503 53.9033 69.4503 34.7252C69.4503 15.547 53.9033 0 34.7252 0C15.547 0 0 15.547 0 34.7252C0 53.9033 15.547 69.4503 34.7252 69.4503ZM19.9661 17.3605C21.5607 17.3605 22.9882 18.0761 23.9449 19.2037L44.6038 12.2071C44.6029 12.177 44.6024 12.1469 44.6024 12.1166C44.6024 10.5163 45.8997 9.21907 47.5 9.21907C49.1003 9.21907 50.3975 10.5163 50.3975 12.1166C50.3975 13.7169 49.1003 15.0142 47.5 15.0142C47.3685 15.0142 47.239 15.0054 47.1122 14.9885L38.3402 28.773C38.6702 29.2435 38.8639 29.8166 38.8639 30.4349C38.8639 31.6362 38.1328 32.6668 37.0913 33.1059L42.229 57.3934C43.7565 57.4748 44.9699 58.7391 44.9699 60.2868C44.9699 61.8871 43.6726 63.1844 42.0724 63.1844C40.4721 63.1844 39.1748 61.8871 39.1748 60.2868C39.1748 59.648 39.3815 59.0576 39.7317 58.5786L21.0924 27.6698C20.7297 27.7496 20.3528 27.7917 19.9661 27.7917C17.0856 27.7917 14.7505 25.4566 14.7505 22.5761C14.7505 19.6956 17.0856 17.3605 19.9661 17.3605ZM45.3228 14.0752L36.6968 27.6302C36.4635 27.5696 36.2187 27.5373 35.9663 27.5373C35.1223 27.5373 34.3626 27.8982 33.833 28.474L24.994 23.9678C25.1164 23.5248 25.1817 23.0581 25.1817 22.5761C25.1817 22.0196 25.0946 21.4835 24.9332 20.9806L45.3228 14.0752ZM24.0999 25.7569L33.0706 30.3302C33.0694 30.365 33.0687 30.3998 33.0687 30.4349C33.0687 31.7204 33.9058 32.8103 35.0647 33.1894L39.5538 54.4108L22.9403 26.8612C23.3812 26.5546 23.7725 26.1817 24.0999 25.7569Z"
                fill="#0091ea"
              />
            </svg>
          </Box>
        </Box>
        <Small>
          &copy; TeamForm<sup>&reg;</sup> Pty. Ltd.
        </Small>
      </FlexItem>
    </Flex>
  );
};

export default InfoSidebarContent;
